import { z } from 'zod'

import {
  TELLER_SESSION_STATUSES,
  TILL_STATUSES,
  TILL_TYPES,
} from '@/features/teller/constants'

/**
 * The teller screens keep their state in the URL, for the reason the customer and group screens do:
 * a filtered view is a place. "Which tills were still unbalanced on Tuesday?" should be a link a
 * supervisor can send, and pressing Back out of a shift should land on the page of the list it was
 * opened from.
 *
 * Two deliberate absences.
 *
 * A **business date is never defaulted here**. The ledger's date comes from
 * `GET /ledger/business-day/current` and it is legitimately null — a branch that has not opened a
 * day has no date at all. A schema that defaulted this to today would put the browser's clock into
 * a filter and quietly show the wrong day's shifts; the screens read the business day and only then
 * decide what to ask for.
 *
 * A **selected customer or account is not in the URL**. At a counter the selection is transient and
 * it identifies a person: it belongs in component state, not in an address bar that gets shared,
 * bookmarked, and logged.
 *
 * Every value is read back through `.catch()`, so a hand-edited or stale URL degrades to the
 * default rather than throwing a teller out of a screen mid-shift.
 */

export const DEFAULT_PAGE_SIZE = 25

/** ISO `2026-08-27`, which is the only form the API accepts for a business date. */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const COUNTER_RAILS = ['deposit', 'security', 'withdrawal', 'repayment'] as const

export type CounterRailId = (typeof COUNTER_RAILS)[number]

export const tellerCounterSearchSchema = z.object({
  rail: z.enum(COUNTER_RAILS).optional().catch(undefined),
})

export type TellerCounterSearch = z.infer<typeof tellerCounterSearchSchema>

export const tellerCashPositionSearchSchema = z.object({
  tillType: z.enum(TILL_TYPES).optional().catch(undefined),
  status: z.enum(TILL_STATUSES).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z.number().int().min(10).max(200).optional().catch(undefined),
})

export type TellerCashPositionSearch = z.infer<typeof tellerCashPositionSearchSchema>

export const tillDetailSearchSchema = z.object({
  /** Absent means "the server's current day", which is not the same as today on this machine. */
  businessDate: isoDate.optional().catch(undefined),
})

export type TillDetailSearch = z.infer<typeof tillDetailSearchSchema>

export const tellerSessionListSearchSchema = z.object({
  tillId: z.string().min(1).optional().catch(undefined),
  businessDate: isoDate.optional().catch(undefined),
  status: z.enum(TELLER_SESSION_STATUSES).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z.number().int().min(10).max(200).optional().catch(undefined),
})

export type TellerSessionListSearch = z.infer<typeof tellerSessionListSearchSchema>

export const SESSION_DETAIL_TABS = ['shift', 'count'] as const

export type SessionDetailTab = (typeof SESSION_DETAIL_TABS)[number]

export const tellerSessionDetailSearchSchema = z.object({
  tab: z.enum(SESSION_DETAIL_TABS).optional().catch(undefined),
})

export type TellerSessionDetailSearch = z.infer<typeof tellerSessionDetailSearchSchema>
